import { useEffect, useMemo, useState } from "react";
import AdminShell from "../components/AdminShell";
import { api } from "../lib/api";
import { LifeBuoy, Loader2, Mail, X, Send, CheckCircle2, MessageSquare } from "lucide-react";

const TABS = [
  { key: "open", label: "Open" },
  { key: "replied", label: "Replied" },
  { key: "closed", label: "Closed" },
  { key: "", label: "All" },
];

const CATEGORY_LABEL = {
  general: "General",
  onboarding: "Onboarding",
  billing: "Billing",
  bug: "Bug",
  other: "Other",
};

function StatusPill({ status }) {
  const map = {
    open:    { cls: "bg-amber-50 text-amber-800 border-amber-200", label: "Open" },
    replied: { cls: "bg-sky-50 text-sky-800 border-sky-200", label: "Replied" },
    closed:  { cls: "bg-neutral-100 text-neutral-700 border-neutral-200", label: "Closed" },
  };
  const m = map[status] || map.open;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}>
      {m.label}
    </span>
  );
}

function TicketDetail({ ticket, onClose, onReplied }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/support-tickets/${ticket.ticket_id}/reply`, { text: reply.trim() });
      onReplied();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't send reply.");
    } finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/admin/support-tickets/${ticket.ticket_id}/status`, { status });
      onReplied();
    } catch (_e) { /* noop */ }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
         data-testid="ticket-detail-modal">
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-950 text-neutral-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-neutral-800 px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold" data-testid="ticket-detail-subject">{ticket.subject}</h3>
              <StatusPill status={ticket.status} />
            </div>
            <div className="mt-0.5 text-xs text-neutral-400">
              {ticket.user_name || ticket.user_email}{ticket.company_name ? ` · ${ticket.company_name}` : ""}
              {" · "}
              <span className="rounded bg-neutral-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-300">
                {CATEGORY_LABEL[ticket.category] || ticket.category || "General"}
              </span>
            </div>
          </div>
          <button onClick={onClose} data-testid="ticket-detail-close"
                  className="rounded p-1.5 text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[420px] overflow-y-auto px-5 py-4">
          <div className="mb-4 rounded-xl border border-neutral-800 bg-neutral-900 p-3">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-amber-400">Original message</div>
            <div className="whitespace-pre-wrap text-sm text-neutral-200">{ticket.message}</div>
            <div className="mt-2 text-[11px] text-neutral-500">
              {new Date(ticket.created_at).toLocaleString()}
            </div>
          </div>

          {(ticket.conversation || []).length > 0 && (
            <details className="mb-4 rounded-xl border border-neutral-800 bg-neutral-900/60 p-3">
              <summary className="cursor-pointer text-xs font-semibold text-neutral-400">
                AI Assistant conversation ({ticket.conversation.length} messages)
              </summary>
              <div className="mt-2 space-y-1.5">
                {ticket.conversation.map((m, i) => (
                  <div key={`${m.ts || ""}-${i}-${m.role}`} className="text-[12px] leading-relaxed">
                    <span className={m.role === "user" ? "text-[#7BD3CE] font-semibold" : "text-amber-400 font-semibold"}>
                      {m.role === "user" ? "Founder" : "Assistant"}:
                    </span>{" "}
                    <span className="text-neutral-300">{m.text}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {(ticket.replies || []).map((r, i) => (
            <div key={`${r.ts || ""}-${i}`} className="mb-3 rounded-xl border border-sky-800/40 bg-sky-950/30 p-3">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-sky-400">
                Reply · {r.author_email}
              </div>
              <div className="whitespace-pre-wrap text-sm text-neutral-200">{r.text}</div>
              <div className="mt-2 text-[11px] text-neutral-500">{new Date(r.ts).toLocaleString()}</div>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="border-t border-neutral-800 bg-neutral-900/50 px-5 py-4">
          <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">Your reply</label>
          <textarea data-testid="ticket-reply-input"
                    value={reply} onChange={e => setReply(e.target.value)}
                    rows={3} required minLength={1} maxLength={4000}
                    placeholder="Type a friendly reply… (the founder will get this by email)"
                    className="mt-1 w-full rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100 outline-none focus:border-amber-500" />
          {err && <p className="mt-1 text-xs text-red-400" data-testid="ticket-reply-error">{err}</p>}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-2">
              <button type="button" disabled={busy} onClick={() => setStatus("closed")}
                      data-testid="ticket-mark-closed"
                      className="rounded-lg border border-neutral-700 bg-transparent px-3 py-1.5 text-xs font-semibold text-neutral-300 hover:bg-neutral-800 disabled:opacity-50">
                Mark as closed
              </button>
              {ticket.status === "closed" && (
                <button type="button" disabled={busy} onClick={() => setStatus("open")}
                        data-testid="ticket-reopen"
                        className="rounded-lg border border-neutral-700 bg-transparent px-3 py-1.5 text-xs font-semibold text-neutral-300 hover:bg-neutral-800 disabled:opacity-50">
                  Reopen
                </button>
              )}
            </div>
            <button type="submit" disabled={busy || !reply.trim()} data-testid="ticket-reply-submit"
                    className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-bold text-neutral-950 hover:bg-amber-400 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send reply
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminSupportTickets() {
  const [tab, setTab] = useState("open");
  const [rows, setRows] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const url = tab ? `/admin/support-tickets?status=${tab}` : "/admin/support-tickets";
        const { data } = await api.get(url);
        if (!cancelled) {
          setRows(data.tickets || []);
          setOpenCount(data.open_count || 0);
        }
      } catch (_e) { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [tab, refreshTick]);

  const refresh = () => setRefreshTick(t => t + 1);

  const tabsRendered = useMemo(() => TABS.map(t => ({
    ...t,
    badge: t.key === "open" ? openCount : undefined,
  })), [openCount]);

  return (
    <AdminShell>
      <div data-testid="admin-support-tickets-page">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold text-neutral-100" data-testid="support-tickets-heading">
              <LifeBuoy className="h-5 w-5 text-amber-400" /> Support tickets
            </h1>
            <p className="text-xs text-neutral-400">Tickets raised by founders via the AI Assistant.</p>
          </div>
        </div>

        <div className="mb-3 inline-flex rounded-lg border border-neutral-800 bg-neutral-900 p-1" data-testid="tickets-tabs">
          {tabsRendered.map(t => (
            <button key={t.key || "all"} onClick={() => setTab(t.key)}
                    data-testid={`tickets-tab-${t.key || "all"}`}
                    className={"flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold " +
                      (tab === t.key ? "bg-amber-500 text-neutral-950" : "text-neutral-400 hover:text-neutral-100")}>
              {t.label}
              {typeof t.badge === "number" && t.badge > 0 && (
                <span className="rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{t.badge}</span>
              )}
            </button>
          ))}
        </div>

        <div className="rounded-2xl border border-neutral-800 bg-neutral-900">
          {loading ? (
            <div className="flex items-center justify-center px-4 py-12 text-sm text-neutral-400">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading tickets…
            </div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-neutral-400" data-testid="tickets-empty">
              <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-neutral-600" />
              No tickets here. Inbox zero.
            </div>
          ) : (
            <div className="divide-y divide-neutral-800">
              {rows.map(t => (
                <button key={t.ticket_id} onClick={() => setActive(t)}
                        data-testid={`ticket-row-${t.ticket_id}`}
                        className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-neutral-800/50">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-amber-400">
                    <MessageSquare className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-semibold text-neutral-100">{t.subject}</span>
                      <StatusPill status={t.status} />
                      <span className="rounded bg-neutral-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-300">
                        {CATEGORY_LABEL[t.category] || t.category || "General"}
                      </span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-neutral-400">
                      <Mail className="mr-1 inline h-3 w-3" />
                      {t.user_email} {t.company_name ? `· ${t.company_name}` : ""}
                    </div>
                    <div className="mt-1 line-clamp-2 text-xs text-neutral-500">{t.message}</div>
                  </div>
                  <div className="shrink-0 text-[11px] text-neutral-500">
                    {new Date(t.created_at).toLocaleDateString()}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {active && (
          <TicketDetail ticket={active}
                        onClose={() => setActive(null)}
                        onReplied={() => { setActive(null); refresh(); }} />
        )}
      </div>
    </AdminShell>
  );
}
